export type SerializedAuthenticatorResponse =
  | {
      clientDataJSON: string;
      attestationObject: string;
    }
  | {
      clientDataJSON: string;
      authenticatorData: string;
      signature: string;
      userHandle?: string;
    };

/** WebAuthn credential 序列化後、normalize 前的輸入形狀（對齊 BE / Yubico JSON） */
export type PasskeyCredentialJsonInput = {
  id: string;
  rawId: string;
  type: PublicKeyCredential['type'];
  response: SerializedAuthenticatorResponse;
  clientExtensionResults?: AuthenticationExtensionsClientOutputs;
  authenticatorAttachment?: AuthenticatorAttachment | null;
};

/** normalize 前允許 clientExtensionResults 缺漏或為 null（瀏覽器 toJSON 行為） */
export type PasskeyCredentialJsonNormalizeInput = Omit<PasskeyCredentialJsonInput, 'clientExtensionResults'> & {
  clientExtensionResults?: AuthenticationExtensionsClientOutputs | null;
};

/** normalize 後保證帶有 clientExtensionResults */
export type NormalizedPasskeyCredentialJson = PasskeyCredentialJsonInput & {
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
};

export interface SerializedPublicKeyCredential {
  id: string;
  rawId: string;
  type: PublicKeyCredential['type'];
  response: SerializedAuthenticatorResponse;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

type PublicKeyCredentialDescriptorJSON = Omit<PublicKeyCredentialDescriptor, 'id'> & {
  id: string | BufferSource;
};

export type PublicKeyCredentialCreationOptionsJSON = Omit<
  PublicKeyCredentialCreationOptions,
  'challenge' | 'user' | 'excludeCredentials'
> & {
  challenge: string | BufferSource;
  user: Omit<PublicKeyCredentialUserEntity, 'id'> & {
    id: string | BufferSource;
  };
  excludeCredentials?: PublicKeyCredentialDescriptorJSON[];
};

export type PublicKeyCredentialRequestOptionsJSON = Omit<
  PublicKeyCredentialRequestOptions,
  'challenge' | 'allowCredentials'
> & {
  challenge: string | BufferSource;
  allowCredentials?: PublicKeyCredentialDescriptorJSON[];
};

export const arrayBufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window.btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
};

export const base64UrlToArrayBuffer = (value: string): ArrayBuffer => {
  const base64 = value.replaceAll('-', '+').replaceAll('_', '/');
  const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const binary = window.atob(paddedBase64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes.buffer;
};

const isAttestationResponse = (response: AuthenticatorResponse): response is AuthenticatorAttestationResponse =>
  'attestationObject' in response;

const isAssertionResponse = (response: AuthenticatorResponse): response is AuthenticatorAssertionResponse =>
  'authenticatorData' in response;

const serializeAuthenticatorResponse = (response: AuthenticatorResponse): SerializedAuthenticatorResponse => {
  const clientDataJSON = arrayBufferToBase64Url(response.clientDataJSON);

  if (isAttestationResponse(response)) {
    return {
      clientDataJSON,
      attestationObject: arrayBufferToBase64Url(response.attestationObject),
    };
  }

  if (!isAssertionResponse(response)) {
    throw new Error('Unsupported authenticator response');
  }

  return {
    clientDataJSON,
    authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
    signature: arrayBufferToBase64Url(response.signature),
    userHandle: response.userHandle ? arrayBufferToBase64Url(response.userHandle) : undefined,
  };
};

export const serializePublicKeyCredential = (credential: PublicKeyCredential): SerializedPublicKeyCredential => ({
  id: credential.id,
  rawId: arrayBufferToBase64Url(credential.rawId),
  type: credential.type,
  response: serializeAuthenticatorResponse(credential.response),
  clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
});

export const deserializeCreationOptions = (
  options: PublicKeyCredentialCreationOptionsJSON,
): PublicKeyCredentialCreationOptions => ({
  ...options,
  challenge: typeof options.challenge === 'string' ? base64UrlToArrayBuffer(options.challenge) : options.challenge,
  user: {
    ...options.user,
    id: typeof options.user.id === 'string' ? base64UrlToArrayBuffer(options.user.id) : options.user.id,
  },
  excludeCredentials: options.excludeCredentials?.map((credential) => ({
    ...credential,
    id: typeof credential.id === 'string' ? base64UrlToArrayBuffer(credential.id) : credential.id,
  })),
});

export const deserializeRequestOptions = (
  options: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions => ({
  ...options,
  challenge: typeof options.challenge === 'string' ? base64UrlToArrayBuffer(options.challenge) : options.challenge,
  allowCredentials: options.allowCredentials?.map((credential) => ({
    ...credential,
    id: typeof credential.id === 'string' ? base64UrlToArrayBuffer(credential.id) : credential.id,
  })),
});

const isAuthenticationExtensionsClientOutputs = (value: unknown): value is AuthenticationExtensionsClientOutputs =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isSerializedAuthenticatorResponse = (value: unknown): value is SerializedAuthenticatorResponse => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  if (!('clientDataJSON' in value) || typeof value.clientDataJSON !== 'string') {
    return false;
  }

  if ('attestationObject' in value && typeof value.attestationObject === 'string') {
    return true;
  }

  return (
    'authenticatorData' in value &&
    typeof value.authenticatorData === 'string' &&
    'signature' in value &&
    typeof value.signature === 'string'
  );
};

const hasPasskeyCredentialIdentityFields = (
  value: object,
): value is Pick<PasskeyCredentialJsonInput, 'id' | 'rawId' | 'type'> => {
  if (!('id' in value) || typeof value.id !== 'string') {
    return false;
  }

  if (!('rawId' in value) || typeof value.rawId !== 'string') {
    return false;
  }

  return 'type' in value && value.type === 'public-key';
};

/** runtime 收窄：DOM 的 PublicKeyCredentialJSON 為 any，需驗證後才視為 PasskeyCredentialJsonInput */
export const isPasskeyCredentialJsonInput = (value: unknown): value is PasskeyCredentialJsonInput => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  if (!hasPasskeyCredentialIdentityFields(value)) {
    return false;
  }

  return 'response' in value && isSerializedAuthenticatorResponse(value.response);
};

/**
 * 補齊 WebAuthn credential JSON 必填欄位
 * @description 部分瀏覽器（如 Mac Safari）的 toJSON() 會省略 clientExtensionResults，導致 BE Yubico 反序列化失敗（130202）
 * @external https://innotech.atlassian.net/browse/IN-142106
 */
export const normalizePublicKeyCredentialJson = (
  payload: PasskeyCredentialJsonNormalizeInput,
): NormalizedPasskeyCredentialJson => ({
  ...payload,
  clientExtensionResults: isAuthenticationExtensionsClientOutputs(payload.clientExtensionResults)
    ? payload.clientExtensionResults
    : {},
});
